import React, { useCallback } from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { roleAccess } from "../../../helpers/RolesPermission";
import {
  PARTICIPANT_PROFILE_TABS,
  USER_ROLE,
  WORKER_PROFILE_TABS,
} from "../../../Constants/constant";
import { parseBooleanVal, selectedTabState } from "../../../Utils/utils";

const DOCUMENTS_TAB_ID = 10;
const WORKER_DOCUMENTS_TAB_ID = 2;

const ComplianceProgressList = ({
  progressBarDetails,
  uuid,
  profileContext = "participant",
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getFormLink = (item) => {
    if (profileContext === "worker") {
      return item.submitted === true
        ? `/worker-profile/${item.cat_slug}/${item.uuid}/update`
        : `/view-template/hrm/${uuid}/${item.cat_slug}/${item.slug}`;
    }

    if (roleAccess([USER_ROLE.Sp_Standard_User])) {
      if (item.submitted === true) {
        return `/participant-profile/${item.cat_slug}/${item.uuid}/view`;
      }
      if (item.slug && item.cat_slug && uuid) {
        return `/view-template/${uuid}/${item.cat_slug}/${item.slug}`;
      }
      return null;
    }

    const preUrl = "sp-profile";
    if (item.submitted === true) {
      return `/${preUrl}/${item.cat_slug}/${item.uuid}/view`;
    }
    return `/view-template/${uuid}/${item.cat_slug}/${item.slug}`;
  };

  const openDocumentsTab = useCallback(
    (item) => {
      if (profileContext === "worker") {
        const documentsTab = WORKER_PROFILE_TABS.find(
          (t) => t.id === WORKER_DOCUMENTS_TAB_ID
        );
        if (documentsTab) {
          selectedTabState.save(JSON.stringify(documentsTab));
        }
        navigate(location.pathname, {
          state: {
            id: WORKER_DOCUMENTS_TAB_ID,
            complianceDocumentName: item.name,
            complianceDocumentUuid: item.submitted ? item.uuid : null,
          },
        });
        return;
      }

      const documentsTab = PARTICIPANT_PROFILE_TABS.find(
        (t) => t.id === DOCUMENTS_TAB_ID
      );
      if (documentsTab) {
        selectedTabState.save(JSON.stringify(documentsTab));
      }
      navigate(location.pathname, {
        state: {
          selectedTab: documentsTab,
          complianceDocumentName: item.name,
          complianceDocumentUuid: item.submitted ? item.uuid : null,
        },
      });
    },
    [location.pathname, navigate, profileContext]
  );

  const visibleItems = (progressBarDetails || [])
    .filter((item) => parseBooleanVal(item.is_compliance))
    .filter((item) => {
      if (item.type === "form") {
        return item.slug !== null && item.slug !== undefined;
      }
      return true;
    })
    .sort((a, b) => (a.name < b.name ? -1 : 1));

  const renderItemLabel = (item) => {
    const labelClass =
      "text-sm text-blue-600 hover:text-blue-800 hover:underline text-left break-words";

    if (item.type === "form") {
      const to = getFormLink(item);
      if (!to) {
        return <span className="text-sm text-gray-700 break-words">{item.name}</span>;
      }
      return (
        <Link to={to} className={labelClass}>
          {item.name}
        </Link>
      );
    }

    return (
      <button
        type="button"
        className={labelClass}
        onClick={() => openDocumentsTab(item)}
      >
        {item.name}
      </button>
    );
  };

  return (
    <div className="w-[min(400px,92vw)] -m-3">
      <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
        <p className="text-sm font-medium text-gray-900 m-0">Onboarding Update</p>
      </div>
      <div
        className="max-h-64 overflow-y-auto overscroll-y-contain bg-white"
        onWheel={(e) => e.stopPropagation()}
      >
        {visibleItems.length > 0 ? (
          visibleItems.map((item, index) => (
            <div
              key={`${item.type}-${item.uuid || item.name}-${index}`}
              className="flex items-start gap-2 px-4 py-2.5 text-gray-700 bg-white hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            >
              {item.submitted ? (
                <CheckCircleIcon
                  className="flex-shrink-0 mt-0.5 h-5 w-5 text-green-500"
                  aria-hidden="true"
                />
              ) : (
                <XCircleIcon
                  className="flex-shrink-0 mt-0.5 h-5 w-5 text-red-400"
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0 flex-1 capitalize">{renderItemLabel(item)}</div>
            </div>
          ))
        ) : (
          <p className="px-4 py-3 text-sm text-gray-500 m-0">
            Assign a compliance profile under Settings → Participant Compliance, then
            save settings.
          </p>
        )}
      </div>
    </div>
  );
};

export default ComplianceProgressList;
